import clsx from "clsx";
import endOfDay from "date-fns/endOfDay";
import isAfter from "date-fns/isAfter";
import isBefore from "date-fns/isBefore";
import isSameDay from "date-fns/isSameDay";
import parseISO from "date-fns/parseISO";
import React, {
  ChangeEvent,
  useCallback,
  useContext,
  useEffect,
  useState
} from "react";
import { shallowEqual, useDispatch } from "react-redux";

import { racehorse360 } from "@tsg/1st-grpc-web-rh360";

import BarnNumberPicker from "components/BarnNumberPicker/BarnNumberPicker";
import { withClickBlocker } from "components/BlockableClickContext";
import { useExamLockerContext } from "components/ExamLockerContext/ExamLockerContext";
import { useLoggedInUser } from "components/LoggedInUserProvider";
import ScrollSyncContext from "components/ScrollSync/ScrollSyncContext";
import StallNumberPicker from "components/StallNumberPicker/StallNumberPicker";
import ExamNote from "components/VetExamsTable/ExamNote";
import TableCellWrapper from "components/VetExamsTable/TableCellWrapper";
import Row from "components/VetExamsTable/TableRow";
import TrainerCommentsPopover, {
  IPopoverItem
} from "components/VetExamsTable/TableRow/CommentsPopover/CommentsPopover";
import RowContent from "components/VetExamsTable/TableRow/RowContent";
import ActionCellsWrapper from "components/VetExamsTable/TableRow/RowContent/ActionCellsWrapper";
import ActionCellCheckbox from "components/VetExamsTable/TableRow/RowContent/ActionCellsWrapper/ActionCellCheckbox";
import AssignActionCell from "components/VetExamsTable/TableRow/RowContent/ActionCellsWrapper/AssignActionCell";
import DateCell from "components/VetExamsTable/TableRow/RowContent/DateCell";
import ExamCell from "components/VetExamsTable/TableRow/RowContent/ExamCell";
import ExamStatusCell from "components/VetExamsTable/TableRow/RowContent/ExamStatusCell";
import FlagCell from "components/VetExamsTable/TableRow/RowContent/FlagCell";
import { getWorkoutRequestFlagPriority } from "components/VetExamsTable/TableRow/RowContent/FlagCell/helper";
import RiskFactorScoreCell from "components/VetExamsTable/TableRow/RowContent/RiskFactorScoreCell";
import TrainerCell from "components/VetExamsTable/TableRow/RowContent/TrainerCell";
import UnAssignExamCell from "components/VetExamsTable/TableRow/RowContent/UnAssignExamCell/UnAssignExamCell";
import { EFlagPriority } from "components/VetExamsTable/helper";
import {
  EExamTableStatus,
  ERowColumn,
  IExamsTableRowData,
  ITableColumnsContent
} from "components/VetExamsTable/types";
import { useRacehorse360Api, useSetQueryData } from "hooks/api";
import { useAppSelector } from "hooks/redux/useAppSelector";
import { useComplicatedRolesAndStatuses } from "hooks/useComplicatedRolesAndStatuses";
import { SortOrder } from "interfaces/SortOrder";
import {
  removeAssignCheckedId,
  setAssignCheckedIds,
  setExamCheckedIds,
  setPassCheckedIds,
  setSavingUnAssignIds,
  setSavingUndoQuickPassIds
} from "store/actions/vetWorkoutsPage";
import { isBeforeDate } from "utils/date-utils";
import {
  WORKOUT_REQUEST_TOTAL_RESULTS_QUERY_KEY,
  checkCurrentWorkoutsTableTab
} from "../../helper";
import { commonWorkoutRequestSelectedFields } from "../options";
import RejectRequestDialog from "./RejectRequestDialog";

import useStyles from "./styles";

interface IProps {
  index: number;
  columnsClassName: string;
  currentRowData: IExamsTableRowData;
  columns: string[];
  onExamClick: (request: IExamsTableRowData) => void;
}

const WorkoutRow = (props: IProps) => {
  const { index, currentRowData, columns, columnsClassName, onExamClick } =
    props;

  const classes = useStyles();
  const dispatch = useDispatch();
  const { currentUser } = useLoggedInUser();
  const scrollSyncState = useContext(ScrollSyncContext);
  const { isLocked, pullLocker } = useExamLockerContext();
  const {
    updateWorkoutRequests,
    filterWorkoutRequests,
    refetchWorkoutRequestsIfOrderedByFlag
  } = useSetQueryData();

  const {
    assignedOnly,
    selectedTab,
    examCheckedIds,
    passCheckedIds,
    assignCheckedIds,
    savingCheckedIds,
    savingUndoQuickPassIds,
    savingUnAssignIds,
    facilityBarns,
    selectedFacility
  } = useAppSelector(state => state?.vetWorkoutsPage, shallowEqual);

  const [isRowRejected, setIsRowRejected] = useState(false);
  const [trainerCommentsPopoverAnchor, setTrainerCommentsPopoverAnchor] =
    useState<HTMLElement>(null);

  const {
    eventId: workoutRequestId,
    eventDate: workoutRequestDate,
    eventFlag: workoutRequestFlag,
    hasEventComment: hasWorkoutRequestComment,
    eventRejectionNote: workoutRequestRejectionNote,
    eventFacility: workoutRequestFacility,
    eventCreationDate: workoutRequestCreationDate,
    eventStatus: workoutRequestStatus,
    examType,
    examStatus,
    examAssignedUser,
    examCreatedBy,
    horse
  } = currentRowData;

  const { isCurrentUserAssigned, isCurrentUserTrackVetAndCreator } =
    useComplicatedRolesAndStatuses({
      assignedUserId: examAssignedUser?.id,
      creatorId: examCreatedBy?.id
    });

  const { isRequestsTab, isExamsTab, isRejectedTab } =
    checkCurrentWorkoutsTableTab(selectedTab.value);
  const isExpiredRequest = isBeforeDate(
    parseISO(workoutRequestDate),
    new Date()
  );
  const isExamChecked = examCheckedIds?.includes(workoutRequestId);
  const isPassChecked = passCheckedIds?.includes(workoutRequestId);
  const isAssignChecked = assignCheckedIds?.includes(workoutRequestId);
  const isUnAssignSaving = savingUnAssignIds?.includes(workoutRequestId);
  const isUndoQuickPassSaving =
    savingUndoQuickPassIds?.includes(workoutRequestId);

  const isRequestsTabCheckBoxDisabled = [isExpiredRequest, !isExamsTab].every(
    Boolean
  );
  const isCheckBoxLoading = savingCheckedIds.includes(workoutRequestId);

  useEffect(() => {
    if (isLocked && isAssignChecked) {
      dispatch(removeAssignCheckedId(workoutRequestId));
    }
  }, [isLocked, isAssignChecked]);

  const {
    useRevertQuickPassWorkoutRequestExam,
    useUnassignWorkoutRequestExam,
    useRevertWorkoutRequest,
    useSetWorkoutRequestFlag,
    useResetWorkoutRequestFlag,
    useSetWorkoutRequestExamReasonNote,
    useResetWorkoutRequestExamReasonNote,
    useListWorkoutRequestComments
  } = useRacehorse360Api();

  const {
    data: listWorkoutRequestComments,
    isLoading: isListWorkoutRequestCommentsLoading
  } = useListWorkoutRequestComments(
    {
      query: {
        workoutRequestIds: [workoutRequestId],
        commentTypes: [
          racehorse360.WorkoutRequestCommentType
            .WORKOUT_REQUEST_COMMENT_TYPE_TRAINER_COMMUNICATION,
          racehorse360.WorkoutRequestCommentType
            .WORKOUT_REQUEST_COMMENT_TYPE_RACING_SECRETARY_COMMUNICATION
        ]
      },
      getOptions: {
        select: [
          "body",
          "updatedOn",
          "updatedBy.firstName",
          "updatedBy.lastName"
        ],
        orderBy: [`updatedOn ${SortOrder.DESC}`]
      }
    },
    {
      enabled: [trainerCommentsPopoverAnchor, workoutRequestId].every(Boolean)
    }
  );

  const preparedCommentData: IPopoverItem[] =
    listWorkoutRequestComments?.workoutRequestComments.map(comment => ({
      id: comment?.id,
      updatedOn: comment?.updatedOn,
      updatedBy: comment?.updatedBy,
      body: comment?.body
    }));

  const { mutateAsync: unassignExam } = useUnassignWorkoutRequestExam();
  const { mutateAsync: revertQuickPass } =
    useRevertQuickPassWorkoutRequestExam();
  const {
    mutateAsync: revertWorkoutRequest,
    isPending: isRevertWorkoutRequestPending
  } = useRevertWorkoutRequest();

  const { mutateAsync: setWorkoutRequestFlag } = useSetWorkoutRequestFlag();
  const { mutateAsync: resetWorkoutRequestFlag } = useResetWorkoutRequestFlag();

  const {
    mutateAsync: setWorkoutRequestExamReasonNote,
    isPending: isSetWorkoutRequestExamReasonNoteLoading
  } = useSetWorkoutRequestExamReasonNote();

  const {
    mutateAsync: resetWorkoutRequestExamReasonNote,
    isPending: isResetWorkoutRequestExamReasonNoteLoading
  } = useResetWorkoutRequestExamReasonNote();

  const isExamReasonNoteLoading = [
    isSetWorkoutRequestExamReasonNoteLoading,
    isResetWorkoutRequestExamReasonNoteLoading
  ].some(Boolean);

  const isQuickPassType =
    examType === racehorse360.HorseExamType.HORSE_EXAM_TYPE_QUICK_PASS;

  const isRequestNotInFuture = [
    isBefore(new Date(), parseISO(workoutRequestDate)),
    isSameDay(new Date(), parseISO(workoutRequestDate))
  ].some(Boolean);

  const isExamNotInProgress = [
    workoutRequestStatus ===
      racehorse360.WorkoutRequestStatus.WORKOUT_REQUEST_STATUS_EXAM_PENDING,
    examStatus === racehorse360.HorseExamStatus.HORSE_EXAM_STATUS_REQUESTED
  ].every(Boolean);

  const canUserRevertExamStatus = [
    currentUser.isChiefVet,
    currentUser.isSeniorVet,
    isCurrentUserTrackVetAndCreator
  ].some(Boolean);

  const userHasAccess = [
    currentUser.isChiefVet,
    currentUser.isSeniorVet,
    isCurrentUserAssigned
  ].some(Boolean);

  const shouldShowFlagPriorityIndicator = [
    workoutRequestFlag,
    isExamsTab || isRequestsTab
  ].every(Boolean);

  const shouldShowFlagOnTrack = [
    isRequestsTab,
    horse.hasActualFlagOnTrack
  ].every(Boolean);

  const shouldShowExamReasonNote = [isRequestsTab, isExamsTab].some(Boolean);

  const shouldShowLastExamNote = [horse.lastExamNote, !isRejectedTab].every(
    Boolean
  );

  const shouldShowExamsTablePPs = [isRequestsTab, horse].every(Boolean);

  const shouldShowRevertTypeButton = [
    isRequestNotInFuture,
    userHasAccess,
    isQuickPassType
  ].every(Boolean);

  const shouldShowRevertStatusButton = [
    !isAfter(new Date(), endOfDay(parseISO(workoutRequestDate))),
    canUserRevertExamStatus,
    isExamNotInProgress
  ].every(Boolean);

  const shouldShowActionCells = [isRequestsTab, isExamsTab].some(Boolean);

  const setExamChecked = (workoutRequestId: string, checked: boolean) => {
    if (checked) {
      dispatch(setExamCheckedIds([...examCheckedIds, workoutRequestId]));
    } else {
      dispatch(
        setExamCheckedIds(
          examCheckedIds.filter(
            examCheckedId => examCheckedId !== workoutRequestId
          )
        )
      );
    }
  };

  const setPassChecked = (workoutRequestId: string, checked: boolean) => {
    if (checked) {
      dispatch(setPassCheckedIds([...passCheckedIds, workoutRequestId]));
    } else {
      dispatch(
        setPassCheckedIds(
          passCheckedIds.filter(
            passCheckedId => passCheckedId !== workoutRequestId
          )
        )
      );
    }
  };

  const setAssignChecked = (workoutRequestId: string, checked: boolean) => {
    if (checked) {
      dispatch(setAssignCheckedIds([...assignCheckedIds, workoutRequestId]));
    } else {
      dispatch(
        setAssignCheckedIds(
          assignCheckedIds.filter(
            assignCheckedId => assignCheckedId !== workoutRequestId
          )
        )
      );
    }
  };

  const handleOpenTrainerCommentsPopover = event => {
    setTrainerCommentsPopoverAnchor(event.target);
  };

  const handleCloseTrainerCommentsPopover = () => {
    setTrainerCommentsPopoverAnchor(null);
  };

  const handleSaveWorkoutRequestComment = async (note: string) => {
    const workoutRequestWithNote = await setWorkoutRequestExamReasonNote({
      id: workoutRequestId,
      body: note.trim(),
      getOptions: {
        select: [
          "examReasonNote.id",
          "examReasonNote.body",
          "examReasonNote.notedOn",
          "examReasonNote.notedBy"
        ]
      }
    });

    handleChangeExamReasonNote(workoutRequestWithNote.examReasonNote);
  };

  const handleDeleteWorkoutRequestComment = async () => {
    await resetWorkoutRequestExamReasonNote({
      id: workoutRequestId
    });

    handleChangeExamReasonNote(null);
  };

  const handleChangePriority = async (event: ChangeEvent<HTMLInputElement>) => {
    const priority = getWorkoutRequestFlagPriority(
      event.currentTarget.value as EFlagPriority
    );
    let updatedWorkoutRequest: racehorse360.IWorkoutRequest;

    if (
      priority ===
      racehorse360.WorkoutRequestFlagPriority
        .WORKOUT_REQUEST_FLAG_PRIORITY_INVALID
    ) {
      updatedWorkoutRequest = await resetWorkoutRequestFlag({
        id: workoutRequestId
      });
    } else {
      updatedWorkoutRequest = await setWorkoutRequestFlag({
        id: workoutRequestId,
        priority,
        getOptions: {
          select: ["flag.priority", "flag.flaggedBy.lastName", "flag.flaggedOn"]
        }
      });
    }

    await updateWorkoutRequests([workoutRequestId], {
      flag: updatedWorkoutRequest.flag
    });
    scrollSyncState.forceRecalculateScroll();
    refetchWorkoutRequestsIfOrderedByFlag();
  };

  const handleExamCheckChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const checked = event.target.checked;

    setExamChecked(workoutRequestId, checked);

    if (checked) {
      setPassChecked(workoutRequestId, false);
    }
  };

  const handlePassCheckChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const checked = event.target.checked;

    setPassChecked(workoutRequestId, checked);

    if (checked) {
      setExamChecked(workoutRequestId, false);
    }
  };

  const uncheckExamPass = (workoutRequestId: string) => {
    setExamChecked(workoutRequestId, false);
    setPassChecked(workoutRequestId, false);
  };

  const handleExamButtonClick = () => {
    onExamClick(currentRowData);
  };

  const handleUnAssignDialogConfirm = () => {
    dispatch(setSavingUnAssignIds([...savingUnAssignIds, workoutRequestId]));

    const clearUnAssignIds = () => {
      dispatch(
        setSavingUnAssignIds(
          savingUnAssignIds.filter(savingUnAssignId => {
            return savingUnAssignId !== workoutRequestId;
          })
        )
      );
    };

    unassignExam({
      id: workoutRequestId,
      getOptions: {
        select: [
          ...commonWorkoutRequestSelectedFields,
          ...selectedTab.additionalSelectedFields
        ]
      }
    })
      .then(() => {
        if (assignedOnly) {
          return filterWorkoutRequests(
            [workoutRequestId],
            WORKOUT_REQUEST_TOTAL_RESULTS_QUERY_KEY
          );
        }

        return updateWorkoutRequests([workoutRequestId], {
          workoutExam: {
            assignedUser: null,
            assignedUserId: null
          }
        });
      })
      .catch(error => {
        console.error("ERROR", error);
      })
      .finally(() => {
        clearUnAssignIds();
      });
  };

  const handleAssignCheckChange = async (isChecked: boolean) => {
    const locker = await pullLocker();
    const isLocked = locker.data.isLocked;

    setAssignChecked(workoutRequestId, isChecked && !isLocked);
  };

  const handleUndoQuickPassConfirm = async () => {
    dispatch(
      setSavingUndoQuickPassIds([...savingUndoQuickPassIds, workoutRequestId])
    );

    const clearUndoQuickPassIds = () => {
      dispatch(
        setSavingUndoQuickPassIds(
          savingUndoQuickPassIds.filter(savingUndoQuickPassId => {
            return savingUndoQuickPassId !== workoutRequestId;
          })
        )
      );
    };

    revertQuickPass({
      id: workoutRequestId
    })
      .then(async workoutRequest => {
        filterWorkoutRequests(
          [workoutRequest.id],
          WORKOUT_REQUEST_TOTAL_RESULTS_QUERY_KEY
        );
      })
      .catch(error => {
        console.error(error);
      })
      .finally(() => {
        clearUndoQuickPassIds();
      });
  };

  const handleChangeExamReasonNote = (
    examReasonNote: racehorse360.IWorkoutRequestExamReasonNote
  ) => {
    updateWorkoutRequests([workoutRequestId], {
      examReasonNote
    });
  };

  const handleRevertWorkoutRequestConfirm = useCallback(async () => {
    revertWorkoutRequest({
      id: workoutRequestId
    })
      .then(async workoutRequest => {
        dispatch(removeAssignCheckedId(workoutRequestId));
        filterWorkoutRequests(
          [workoutRequest.id],
          WORKOUT_REQUEST_TOTAL_RESULTS_QUERY_KEY
        );
      })
      .catch(error => console.error(error));
  }, []);

  const handleBarnChange = (horse: racehorse360.IHorse) => {
    updateWorkoutRequests([workoutRequestId], {
      horse: {
        barn: horse.barn,
        stallNumber: horse.stallNumber
      }
    });
  };

  const handleStallChange = (patch: racehorse360.IHorse) => {
    updateWorkoutRequests([workoutRequestId], {
      horse: {
        stallNumber: patch.stallNumber
      }
    });
  };

  const columnsContent: ITableColumnsContent = {
    [ERowColumn.HORSE_AND_TRAINER]: (
      <TrainerCell
        key={`${workoutRequestId}-trainer`}
        bold={isRequestsTab}
        trainerInfo={horse.trainer}
        shouldDisplayComments={
          hasWorkoutRequestComment && (isRequestsTab || isRejectedTab)
        }
        onCommentIconClick={handleOpenTrainerCommentsPopover}
      />
    ),
    [ERowColumn.WORK_DATE]: (
      <DateCell
        key={`${workoutRequestId}-work-date`}
        date={workoutRequestDate}
        bold={isRequestsTab}
      />
    ),
    [ERowColumn.REQUESTED]: (
      <DateCell
        key={`${workoutRequestId}-creation-date`}
        date={workoutRequestCreationDate}
        bold={isRequestsTab}
      />
    ),
    [ERowColumn.EXAM_STATUS]: (
      <ExamStatusCell
        key={`${workoutRequestId}-exam-status`}
        status={isExamNotInProgress && EExamTableStatus.PENDING}
        horseName={horse.name}
        eventName={"Workout Request"}
        eventDate={workoutRequestDate}
        eventTabName={"Requests"}
        shouldShowRevertButton={shouldShowRevertStatusButton}
        revertButtonDataTestId={"revertWorkoutRequestButton"}
        onConfirm={handleRevertWorkoutRequestConfirm}
        isRevertDecisionLoading={isRevertWorkoutRequestPending}
        centered={true}
      />
    ),
    [ERowColumn.UNASSIGN]: (
      <UnAssignExamCell
        key={`${workoutRequestId}-un-assign`}
        rowData={currentRowData}
        onConfirm={handleUnAssignDialogConfirm}
        isUnAssignSaving={isUnAssignSaving}
      />
    ),
    [ERowColumn.EXAM]: (
      <ExamCell
        key={`${workoutRequestId}-exam`}
        assignedUserId={examAssignedUser?.id}
        onExamButtonClick={handleExamButtonClick}
      />
    ),
    [ERowColumn.EXAM_TYPE]: (
      <ExamStatusCell
        key={`${workoutRequestId}-exam-type`}
        status={isQuickPassType && EExamTableStatus.QUICK_PASSED}
        horseName={horse.name}
        eventName={"Workout Request"}
        eventDate={workoutRequestDate}
        eventTabName={"Requests"}
        shouldShowRevertButton={shouldShowRevertTypeButton}
        revertButtonDataTestId={"revertWorkoutRequestButton"}
        onConfirm={handleUndoQuickPassConfirm}
        isRevertDecisionLoading={isUndoQuickPassSaving}
      />
    ),
    [ERowColumn.FLAG_REQUEST]: (
      <FlagCell
        key={`${workoutRequestId}-flag-request`}
        flag={workoutRequestFlag}
        facility={workoutRequestFacility}
        onChangePriority={handleChangePriority}
      />
    ),
    [ERowColumn.IMAGE_DIAGNOSTICS_REQUIRED]: (
      <TableCellWrapper key={`${workoutRequestId}-diagnostics`} centered>
        {workoutRequestRejectionNote?.isImagingDiagnosticsRequired
          ? "Yes"
          : "No"}
      </TableCellWrapper>
    ),
    [ERowColumn.RISK_FACTOR_SCORE]: (
      <RiskFactorScoreCell
        key={`${workoutRequestId}-risk-factor-score`}
        value={horse.riskFactorScore}
      />
    ),
    [ERowColumn.BARN]: (
      <TableCellWrapper key={`${workoutRequestId}-barn`} bold={isRequestsTab}>
        <BarnNumberPicker
          className={classes.optionPicker}
          selectedFacilityId={selectedFacility?.id}
          horse={horse}
          options={facilityBarns}
          endActions={true}
          onBarnChange={handleBarnChange}
        />
      </TableCellWrapper>
    ),
    [ERowColumn.STALL]: (
      <TableCellWrapper key={`${workoutRequestId}-stall`} bold={isRequestsTab}>
        <StallNumberPicker
          className={classes.optionPicker}
          selectedFacilityId={selectedFacility?.id}
          horse={horse}
          endActions={true}
          onStallChange={handleStallChange}
        />
      </TableCellWrapper>
    )
  };

  const actionCells = shouldShowActionCells && (
    <ActionCellsWrapper
      className={clsx({
        [classes.rowExamChecked]: isExamChecked,
        [classes.rowPassChecked]: isPassChecked,
        [classes.rowRejected]: isRowRejected,
        [classes.rowAssignChecked]: isAssignChecked,
        [classes.rowOdd]: index % 2
      })}
    >
      {isRequestsTab && (
        <>
          <ActionCellCheckbox
            className={clsx({
              [classes.menuButtonExamChecked]: isExamChecked
            })}
            checked={isExamChecked}
            isLoading={isCheckBoxLoading}
            disabled={isRequestsTabCheckBoxDisabled}
            onChange={handleExamCheckChange}
            dataTestId={"examCheckbox"}
          />
          <ActionCellCheckbox
            className={clsx({
              [classes.menuButtonPassChecked]: isPassChecked
            })}
            checked={isPassChecked}
            isLoading={isCheckBoxLoading}
            disabled={isRequestsTabCheckBoxDisabled}
            onChange={handlePassCheckChange}
            dataTestId={"quickPassCheckbox"}
          />
          <RejectRequestDialog
            workoutRequestId={workoutRequestId}
            horse={horse}
            isRowRejected={isRowRejected}
            setIsRowRejected={setIsRowRejected}
            disabled={isRequestsTabCheckBoxDisabled}
            uncheckExamPass={uncheckExamPass}
          />
        </>
      )}
      {isExamsTab && (
        <AssignActionCell
          assignedUserId={examAssignedUser?.id}
          isAssignChecked={isAssignChecked}
          onAssignChange={handleAssignCheckChange}
          examStatus={examStatus}
          isLoading={isCheckBoxLoading}
        />
      )}
    </ActionCellsWrapper>
  );

  const examReasonNoteContent = shouldShowExamReasonNote && (
    <ExamNote
      index={index}
      additionalDesktopClassNames={clsx({
        [classes.rowExamChecked]: isExamChecked,
        [classes.rowPassChecked]: isPassChecked,
        [classes.rowAssignChecked]: isAssignChecked
      })}
      rowData={currentRowData}
      isLoading={isExamReasonNoteLoading}
      readOnly={isExamsTab}
      disabled={isExpiredRequest}
      onSaveComment={handleSaveWorkoutRequestComment}
      onDeleteComment={handleDeleteWorkoutRequestComment}
    />
  );

  return (
    <>
      <Row
        index={index}
        rowContainerClassName={clsx({
          [classes.rowExamChecked]: isExamChecked,
          [classes.rowPassChecked]: isPassChecked,
          [classes.rowAssignChecked]: isAssignChecked,
          [classes.rowRejected]: isRowRejected
        })}
        rowContentClassName={columnsClassName}
        rowData={currentRowData}
        examNoteContent={examReasonNoteContent}
        actionCellsContent={actionCells}
        shouldShowFlagPriorityIndicator={shouldShowFlagPriorityIndicator}
        shouldShowFlagOnTrack={shouldShowFlagOnTrack}
        shouldShowLastExamNote={shouldShowLastExamNote}
        shouldShowExamsTablePPs={shouldShowExamsTablePPs}
        shouldShowRejectionNote={isRejectedTab}
      >
        <RowContent
          rowData={currentRowData}
          columns={columns}
          bold={isRequestsTab}
          customColumnsContent={columnsContent}
          extenderFitsTwoColumns={isRequestsTab}
          extenderFitsOneColumns={isExamsTab}
          facilityBarns={facilityBarns}
          selectedFacilityId={selectedFacility?.id}
        />
      </Row>

      {Boolean(trainerCommentsPopoverAnchor) && (
        <TrainerCommentsPopover
          anchorElement={trainerCommentsPopoverAnchor}
          onClose={handleCloseTrainerCommentsPopover}
          isLoadingData={isListWorkoutRequestCommentsLoading}
          data={preparedCommentData}
        />
      )}
    </>
  );
};

export default React.memo(withClickBlocker<IProps>(WorkoutRow));
